import type { AxiosInstance } from 'axios';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';
import { z } from 'zod';

import { CANARY_COOKIE_NAME } from 'lib/apiHelpers';
import { notify } from 'lib/errorLogger';
import { ApiSchemaError, SCHEMA_TOAST_STYLE } from 'services/api/errors';
import type { ApiConfig } from 'services/api/setup';
import type { EndpointBase, EndpointScope, RuntimeArgs } from 'services/api/endpointTypes';

export async function executeRequest(
  client: AxiosInstance,
  getCfg: () => ApiConfig,
  epConfig: EndpointBase,
  args: RuntimeArgs,
): Promise<unknown> {
  const cfg = getCfg();
  const scope: EndpointScope = epConfig.scope ?? 'authenticated';
  const { path, query, body } = args;

  const headers: Record<string, string> = { ...epConfig.headers };
  const canary = Cookies.get(CANARY_COOKIE_NAME);
  if (canary) headers[CANARY_COOKIE_NAME] = canary;

  if (scope === 'authenticated') {
    Object.assign(headers, await cfg.resolveAuthHeaders());
  }

  // Defensive URL build: if `url` is a function and `path` is malformed
  // (e.g. JS caller bypassing types), the builder can throw a TypeError
  // BEFORE Zod validation runs — that would bypass ApiSchemaError reporting.
  // Catch the build failure into a placeholder so the path validation below
  // reaches its proper ApiSchemaError path. We never make an HTTP request
  // with a broken URL because validateInput throws first — but that promise
  // only holds when a path schema is declared. The runtime guard below
  // enforces the same invariant the discriminated EndpointBase encodes at
  // compile time, for `as any` / JS callers that bypass typing.
  let url: string;
  try {
    url = typeof epConfig.url === 'function' ? epConfig.url(path) : epConfig.url;
  } catch {
    if (typeof epConfig.url === 'function' && !epConfig.path) {
      throw new Error(
        'endpoint(): function url requires a path schema so validateInput can run before the HTTP request',
      );
    }
    url = '<unresolved url — path validation will report the cause>';
  }

  // Side-effect: log to console (always), show a visually-distinct toast
  // (SCHEMA_TOAST_STYLE — out-of-palette colours since per-toast `position`
  // is ignored by the project's DraggableToastContainer wrapper) in any
  // non-prod build, then return an ApiSchemaError instance for the caller
  // to throw. Inputs always throw via validateInput; response throws in
  // non-prod and is silent (telemetry-only) in production.
  const reportSchemaMismatch = (
    section: 'path' | 'query' | 'body' | 'response',
    zodError: z.ZodError,
  ) => {
    // Pass method/url as %s args (not interpolated into the format string) so a
    // url containing a console format specifier (%s/%c/…) can't forge or shift
    // the log output (CWE-134).
    console.error(
      '%c[API SCHEMA] %s %s',
      'background:#c00;color:#fff;padding:2px 6px;font-weight:bold;border-radius:3px',
      epConfig.method,
      url,
      `\n  section: ${section}\n  issues:`,
      zodError.issues,
    );
    if (process.env.NODE_ENV !== 'production') {
      toast.error(
        `API schema mismatch: ${section} on ${epConfig.method} ${url} — see console`,
        { autoClose: false, style: SCHEMA_TOAST_STYLE },
      );
    }
    return new ApiSchemaError(section, epConfig.method, url, zodError);
  };

  // Input validation: inputs come from our own code, so any mismatch is a
  // bug regardless of env — always throw. Cheap (sub-ms) given Zod and well
  // below HTTP round-trip cost.
  const validateInput = (
    section: 'path' | 'query' | 'body',
    schema: z.ZodTypeAny | undefined,
    value: unknown,
  ) => {
    if (!schema) return;
    const parsed = schema.safeParse(value);
    if (!parsed.success) throw reportSchemaMismatch(section, parsed.error);
  };

  validateInput('path', epConfig.path, path);
  validateInput('query', epConfig.query, query);
  validateInput('body', epConfig.body, body);

  const response = await client.request({
    baseURL: cfg.baseURL,
    method: epConfig.method,
    url,
    headers,
    params: {
      ...(query ?? {}),
      'x-dokka-app-version': process.env.REACT_APP_BUILD_VERSION,
      'x-dokka-app-env': process.env.REACT_APP_ENV,
    },
    data: body,
  });

  // Response validation: throw in non-prod builds (loud failures surface
  // schema drift during refactor), silent + telemetry in production builds
  // (drift visible without breaking user-facing features). If a schema goes
  // out of sync with the backend in a path that non-prod builds didn't
  // exercise, prod stays functional and we discover the drift via Sentry
  // rather than via broken pages.
  const parsed = epConfig.response.safeParse(response.data);
  if (parsed.success) return parsed.data;

  const err = reportSchemaMismatch('response', parsed.error);

  if (process.env.NODE_ENV !== 'production') throw err;

  // Shape-only outline — no raw values. Bank-feed responses can include
  // account numbers / large grids, and Sentry already has zodError.issues
  // (path + expected + received types) to pinpoint the failed field. If a
  // concrete value is needed for debugging, reproduce the call locally.
  const d = response.data;
  const responseShape = Array.isArray(d)
    ? { type: 'array', length: d.length }
    : d && typeof d === 'object'
      ? { type: 'object', keys: Object.keys(d) }
      : { type: typeof d };

  notify.captureEvent(
    parsed.error,
    { data: { method: epConfig.method, url, responseShape } },
    'api-zod-drift',
  );
  return response.data;
}
